import { useMemo, useRef, useState } from "react"
import { describeError } from "../../api/http"
import { catalogWrites } from "../../api/mutations"
import { fetchGroupCatalogs, useNotReadyToDeploy } from "../../api/queries"
import { DeployReason, type Catalog, type NotReadyToDeployGroup } from "../../api/types"
import { Note } from "../../components/Note"
import { useConfirm } from "../../hooks/useConfirm"
import { REASONS } from "./tables"

/**
 * «Что загрузка пропустит»: проверка готовности каталога и починка вывода.
 *
 * Запрос дорогой — поднимает весь каталог и весь состав сайта, — поэтому идёт
 * только по кнопке и держится в кэше.
 */
export default function NotReady({
	catalog,
	siteCatalog,
	websiteId,
	disabled,
}: {
	catalog: Catalog
	siteCatalog: Catalog
	websiteId: number
	disabled: boolean
}) {
	const [asked, setAsked] = useState(false)
	const rows = useNotReadyToDeploy(catalog.id, websiteId, asked)

	return (
		<div className="pub-ready">
			<div className="pub-ready__head">
				<b>Что загрузка пропустит</b>
				<button
					className="btn"
					disabled={rows.isFetching}
					onClick={() => (asked ? void rows.refetch() : setAsked(true))}
				>
					{rows.isFetching ? "Считаю…" : "Проверить готовность"}
				</button>
				<span className="muted">
					поднимает весь каталог и весь состав сайта — на стенде 2 934 строки
					за 0,8 с
				</span>
			</div>

			{rows.isError && (
				<Note tone="error">{describeError(rows.error)}</Note>
			)}

			{rows.data && (
				<>
					<ShowStatusRepair
						siteCatalog={siteCatalog}
						rows={rows.data}
						disabled={disabled}
						onDone={() => void rows.refetch()}
					/>
					<NotReadyTable rows={rows.data} />
				</>
			)}
		</div>
	)
}

/**
 * Прописать вывод группам, которым его не прописали.
 *
 * Рижский интерфейс галкой «выводить в каталоге» не управляет: у одной группы
 * она проставляется сама при загрузке (`components/site/GroupSitePanel.tsx`).
 * Массовой загрузке этого мало — она молча пропускает все группы каталога сайта
 * со снятой галкой, а их бывают тысячи (у `www.avtodelo.lt` — 2 927). Починить
 * их одним запросом нельзя: бэк принимает `change-show-status` по одной группе
 * за раз и требует её строки каталога, то есть на группу выходит два запроса.
 *
 * Поэтому кнопка, а не автоматика: на живом сайте это разом выводит тысячи
 * скрытых групп, и решение должно быть осознанным. Отменить его в интерфейсе
 * нечем — обратная кнопка убрана вместе с самой системой.
 */
function ShowStatusRepair({
	siteCatalog,
	rows,
	disabled,
	onDone,
}: {
	siteCatalog: Catalog
	rows: NotReadyToDeployGroup[]
	disabled: boolean
	onDone: () => void
}) {
	const { ask, dialog } = useConfirm()
	const [running, setRunning] = useState(false)
	const [done, setDone] = useState(0)
	const [failed, setFailed] = useState<{ id: string; error: string }[]>([])
	const stop = useRef(false)

	const targets = useMemo(
		() => rows.filter(row => row.reason === DeployReason.ShowStatus),
		[rows]
	)

	if (targets.length === 0) return null

	const word = targets.length === 1 ? "группе" : "группам"

	async function start() {
		const yes = await ask({
			title: `Прописать вывод ${targets.length} ${word}?`,
			text: `После этого массовая загрузка перестанет их пропускать и все они уедут на ${siteCatalog.name}. Если часть групп скрыта намеренно — вернуть их в интерфейсе будет нечем.`,
			confirm: "Прописать",
		})
		if (!yes) return

		setRunning(true)
		setDone(0)
		setFailed([])
		stop.current = false

		const bad: { id: string; error: string }[] = []
		let index = 0
		for (const row of targets) {
			if (stop.current) break
			try {
				const catalogs = await fetchGroupCatalogs(row.productGroupId)
				const mine = catalogs.filter(item => item.catalogId === siteCatalog.id)
				if (mine.length > 0) {
					await catalogWrites.setShowStatus(
						mine.map(item => ({
							productGroupId: item.productGroupId,
							catalogId: item.catalogId,
							webCategoryId: item.webCategoryId,
							catalogCategoryId: item.catalogCategoryId,
							showStatus: true,
						}))
					)
				}
			} catch (thrown) {
				bad.push({ id: row.productGroupId, error: describeError(thrown) })
			}
			setDone(++index)
		}

		setFailed(bad)
		setRunning(false)
		onDone()
	}

	return (
		<Note tone="warn">
			<b>
				{targets.length} {word} каталога «{siteCatalog.name}» не прописан вывод
			</b>{" "}
			— массовая загрузка их пропустит. У Риги это не настройка, а недоделка:
			интерфейс может прописать вывод всем сразу, по два запроса на группу.
			<div className="pub-actions">
				{!running && (
					<button
						className="btn"
						disabled={disabled}
						onClick={() => void start()}
					>
						Прописать вывод всем
					</button>
				)}
				{running && (
					<>
						<span className="spinner" />
						<span className="muted">
							{done} из {targets.length}
						</span>
						<button className="btn" onClick={() => (stop.current = true)}>
							Остановить
						</button>
					</>
				)}
			</div>

			{dialog}

			{failed.length > 0 && (
				<Note tone="error">
					Не удалось прописать {failed.length}:{" "}
					{failed
						.slice(0, 3)
						.map(item => `${item.id} — ${item.error}`)
						.join("; ")}
					{failed.length > 3 && " …"}
				</Note>
			)}
		</Note>
	)
}

/** Список групп, которые массовая загрузка пропустит. Их тысячи (у
 *  `www.avtodelo.lt` — 2 934 из 3 226 групп каталога), поэтому с поиском,
 *  разбивкой по причинам и ограничением показа. */
function NotReadyTable({ rows }: { rows: NotReadyToDeployGroup[] }) {
	const LIMIT = 200
	const [search, setSearch] = useState("")

	const found = useMemo(() => {
		const needle = search.trim().toLowerCase()
		if (!needle) return rows
		return rows.filter(
			row =>
				row.productGroupId.toLowerCase().includes(needle) ||
				(row.name ?? "").toLowerCase().includes(needle)
		)
	}, [rows, search])

	const byReason = useMemo(() => {
		const counts = new Map<DeployReason, number>()
		for (const row of rows) {
			counts.set(row.reason, (counts.get(row.reason) ?? 0) + 1)
		}
		return [...counts.entries()].sort((a, b) => b[1] - a[1])
	}, [rows])

	if (rows.length === 0) {
		return <Note tone="ok">Все группы каталога готовы к загрузке.</Note>
	}

	return (
		<>
			<div className="pub-ready__stats">
				{byReason.map(([reason, count]) => (
					<span key={reason} className="tag tag--muted">
						{REASONS[reason]} — {count}
					</span>
				))}
			</div>

			<input
				className="input"
				placeholder="Поиск по коду или названию"
				value={search}
				onChange={event => setSearch(event.target.value)}
			/>

			<div className="pub-ready__wrap">
				<table className="products">
					<thead>
						<tr>
							<th className="col-code">Код</th>
							<th>Название</th>
							<th className="col-reason">Причина</th>
						</tr>
					</thead>
					<tbody>
						{found.slice(0, LIMIT).map(row => (
							<tr key={row.productGroupId}>
								<td className="mono">{row.productGroupId}</td>
								<td>{row.name}</td>
								<td className="muted">{REASONS[row.reason]}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className="muted">
				{found.length > LIMIT
					? `показаны первые ${LIMIT} из ${found.length}`
					: `строк: ${found.length}`}
			</div>
		</>
	)
}
